import React, { useEffect, useRef } from "react";
import {
  getFormattedNames,
  getHeroNameFromSelectedTree,
  getHeroTalentIconURL,
  getParsedSelectedTalents,
  getSpecOrClassIcon,
  getSpritesheetUrl,
} from "../../../lib/wowHelpers";
import Icon from "../../Common/Icon";
import TalentTreeHeader from "./TalentTreeHeader";
import { MediaQuery } from "@outplayed/responsive";
import OctagonTalentIcon from "../../Common/OctagonTalentIcon";
import "react-tooltip/dist/react-tooltip.css";
import WowTooltip from "components/Tooltips/WowTooltip";

const StandardTreeHeader = ({ treeType, classType, specType, selectedHero }) => {
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);
  const heroName = getHeroNameFromSelectedTree(selectedHero, displayClassName, displaySpecName);
  const treeLabel = treeType === "spec" ? displaySpecName : treeType === "class" ? displayClassName : heroName;
  const iconImg =
    treeType === "hero" ? getHeroTalentIconURL(heroName) : getSpecOrClassIcon(treeType == "class", dataClassName, dataSpecName);

  return (
    <div className="standard-tree-header-container">
      <Icon iconSize="20px" classType={"circle"} staticDataImg={iconImg} />
      <div className="tree-label">{treeLabel}</div>
    </div>
  );
};

const WOWTalentTreeSection = (props) => {
  const {
    classAndSpecTree,
    classType,
    specType,
    selectedTalents,
    className,
    hideHeader,
    selectedHero,
    treeHeaderComponent = StandardTreeHeader,
  } = props;

  return (
    <div className={`wow-talent-tree-container ${className ?? ""}`}>
      {!hideHeader && <TalentTreeHeader {...props} />}
      <MediaQuery min="MOBILE_SMALL" max="TABLET">
        <div className="wow-talent-tree-section">
          <WOWHeroTalentTreeContainer
            tree={classAndSpecTree?.hero[selectedHero].nodes ?? {}}
            specType={specType}
            classType={classType}
            treeType="hero"
            selectedTalentsData={selectedTalents}
            treeHeaderComponent={treeHeaderComponent}
            selectedHero={selectedHero}
          />
          <WOWTalentTreeContainer
            tree={classAndSpecTree?.classNodes ?? {}}
            specType={specType}
            classType={classType}
            treeType="class"
            selectedTalentsData={selectedTalents}
            treeHeaderComponent={treeHeaderComponent}
          />
          <WOWTalentTreeContainer
            tree={classAndSpecTree?.specNodes ?? {}}
            specType={specType}
            classType={classType}
            treeType="spec"
            selectedTalentsData={selectedTalents}
            treeHeaderComponent={treeHeaderComponent}
          />
        </div>
      </MediaQuery>
      <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE">
        <div className="wow-talent-tree-section">
          <WOWTalentTreeContainer
            tree={classAndSpecTree?.classNodes ?? {}}
            specType={specType}
            classType={classType}
            treeType="class"
            selectedTalentsData={selectedTalents}
            treeHeaderComponent={treeHeaderComponent}
          />
          <WOWHeroTalentTreeContainer
            tree={classAndSpecTree?.hero[selectedHero].nodes ?? {}}
            specType={specType}
            classType={classType}
            treeType="hero"
            selectedTalentsData={selectedTalents}
            treeHeaderComponent={treeHeaderComponent}
            selectedHero={selectedHero}
          />
          <WOWTalentTreeContainer
            tree={classAndSpecTree?.specNodes ?? {}}
            specType={specType}
            classType={classType}
            treeType="spec"
            selectedTalentsData={selectedTalents}
            treeHeaderComponent={treeHeaderComponent}
          />
        </div>
      </MediaQuery>
    </div>
  );
};

const drawLine = (ctx, canvasRef, x1, y1, x2, y2, hero) => {
  let path = new Path2D();
  let c = canvasRef.current;

  // single_width and single_height are constants from the css file wow-build
  const single_width = hero ? 24 : 23;
  const single_height = hero ? 55 : 45;

  // The ratio for each tree is determined by taking (the html canvas width and height) / (the css width and height)
  const ratio = hero ? 1 : 2.44;

  // TODO: the drawing gets rendered first and then stretched by the appropriate ratio. At larger image sizes like the normal talent trees
  // the image is getting to stretched width wise so the left and right parts of the tree are a little to off center when it comes to the center of the icon.
  // I think this can get resolved via reduicing how wide the single width is and then stretching the x ratio more but need to investigate more.
  const startX = (single_width * x1 - single_width / 2) * ratio;
  const startY = (single_height * y1 - single_height / 1.5) * ratio;
  const endX = (single_width * x2 - single_width / 2) * ratio;
  const endY = (single_height * y2 - single_height / 1.5) * ratio;

  c.width = c.clientWidth;
  c.height = c.clientHeight;

  path.moveTo(startX, startY);
  path.lineTo(endX, endY);

  return path;
};

const WOWHeroTalentTreeContainer = (props) => {
  const { tree, treeType, classType, specType, selectedTalentsData, treeHeaderComponent: Header, selectedHero } = props;

  const { selectedTalents, selectedTalentNodeIds, selectedTalentsValues } = getParsedSelectedTalents(selectedTalentsData);

  const canvasRef = useRef(null);
  useEffect(() => {
    var ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    const paths = Object.entries(tree)
      .map(([key, node]) => {
        const prevNodes = node.previousNodeIds.map((nodeId) => {
          return tree[nodeId];
        });

        return prevNodes.map((prevNode) => {
          const selectedTalentsArray = selectedTalents;
          let lineColor = "#454582";
          const lineIsYellow = "#FFAF33";

          if (node.spells.length > 0 && prevNode.spells.length > 0) {
            if (selectedTalentsArray.includes(prevNode.id.toString()) && selectedTalentsArray.includes(node.id.toString())) {
              lineColor = lineIsYellow;
            }
          }
          return [drawLine(ctx, canvasRef, prevNode.column, prevNode.row + 1, node.column, node.row + 1, true), lineColor];
        });
      })
      .flat();

    // Okay the width here is a value of DIPS instead of px so the value will dynamicly change depending on the Device pixel ratio.
    ctx.canvas.width = 158;
    ctx.canvas.height = 275;
    // width="1000" height="1100"
    // width="139" height="229"
    ctx.lineWidth = 2;
    for (let i = 0; i < paths.length; i++) {
      const path = paths[i];
      ctx.strokeStyle = path[1];
      ctx.stroke(path[0]);
    }
    ctx.closePath();
  }, [tree, selectedTalentsData]);

  const spriteSheet = getSpritesheetUrl(classType, specType, treeType);
  return (
    <div className="tree-container hero-tree-container">
      <Header treeType={treeType} classType={classType} specType={specType} selectedHero={selectedHero} />
      <div className="hero-talent-tree">
        {Object.entries(tree).map(([key, node], index) => {
          const classType = node.type;
          if (node.spells.length > 0) {
            if (node.spells.length > 1) {
              // Normal selectable spell node.
              for (let i = 0; i < node.spells.length; i++) {
                // Use nodeEntryId for data from WCL and the definition ID for data from the Blizzard API.
                if (
                  selectedTalentNodeIds.includes(node.spells[i].nodeEntryId.toString()) ||
                  selectedTalentNodeIds.includes(node.spells[i].definitionId.toString())
                ) {
                  return (
                    <GridIcon
                      key={index + node.spells[i].nodeEntryId.toString()}
                      gridRow={(node.row + 1).toString()}
                      gridColumn={node.column.toString()}
                      classType={classType}
                      // iconSize={"25px"}
                      type={"spell"}
                      color={selectedTalents.includes(node.id.toString()) ? "#FF9B00" : null}
                      talentTreeGreyout={!selectedTalents.includes(node.id.toString())}
                      talentSelectedNumber={selectedTalentsValues[selectedTalents.indexOf(node.id.toString())]}
                      talentMaxNumber={node.spells[i].maxRanks.toString()}
                      talentName={node.spells[i].name}
                      talentDescription={
                        node.spells[i].rankDescriptions[
                          parseInt(selectedTalentsValues[selectedTalents.indexOf(node.id.toString())]) - 1
                        ]
                      }
                      id={node.spells[i].spellId}
                      spriteSheet={spriteSheet}
                      sprite={node.spells[i].sprite}
                    />
                  );
                }
              }
            }
            return (
              <GridIcon
                key={index + node.spells[0].nodeEntryId.toString()}
                gridRow={(node.row + 1).toString()}
                gridColumn={node.column.toString()}
                classType={classType}
                type={"spell"}
                // iconSize={"25px"}
                color={selectedTalents.includes(node.id.toString()) ? "#FF9B00" : null}
                talentTreeGreyout={!selectedTalents.includes(node.id.toString())}
                talentSelectedNumber={selectedTalentsValues[selectedTalents.indexOf(node.id.toString())]}
                talentMaxNumber={node.spells[0].maxRanks.toString()}
                talentName={node.spells[0].name}
                talentDescription={
                  selectedTalents.includes(node.id.toString())
                    ? node.spells[0].rankDescriptions[
                        parseInt(selectedTalentsValues[selectedTalents.indexOf(node.id.toString())]) - 1
                      ]
                    : node.spells[0].description
                }
                talentDef={node.spells[0].definitionId.toString()}
                id={node.spells[0].spellId}
                spriteSheet={spriteSheet}
                sprite={node.spells[0].sprite}
              />
            );
          }
          return (
            <GridIcon key={index} gridRow={(node.row + 1).toString()} gridColumn={node.column.toString()} classType={classType} />
          );
        })}
        <canvas className="talent-tree-canvas" ref={canvasRef} width="154" height="275" />
      </div>
    </div>
  );
};

const WOWTalentTreeContainer = (props) => {
  const { tree, treeType, classType, specType, selectedTalentsData, treeHeaderComponent: Header } = props;

  const { selectedTalents, selectedTalentNodeIds, selectedTalentsValues } = getParsedSelectedTalents(selectedTalentsData);

  const canvasRef = useRef(null);
  useEffect(() => {
    var ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    const paths = Object.entries(tree)
      .map(([key, node]) => {
        const prevNodes = node.previousNodeIds.map((nodeId) => {
          return tree[nodeId];
        });

        return prevNodes.map((prevNode) => {
          const selectedTalentsArray = selectedTalents;
          let lineColor = "#454582";
          let lineIsYellow = "#FFAF33";

          if (node.spells.length > 0 && prevNode.spells.length > 0) {
            if (selectedTalentsArray.includes(prevNode.id.toString()) && selectedTalentsArray.includes(node.id.toString())) {
              lineColor = lineIsYellow;
            }
          }
          return [drawLine(ctx, canvasRef, prevNode.column, prevNode.row + 1, node.column, node.row + 1, false), lineColor];
        });
      })
      .flat();

    ctx.canvas.width = 1000;
    ctx.canvas.height = 1100;
    ctx.lineWidth = 4;
    for (let i = 0; i < paths.length; i++) {
      const path = paths[i];
      ctx.strokeStyle = path[1];
      ctx.stroke(path[0]);
    }
    ctx.closePath();
  }, [tree, selectedTalentsData]);

  const spriteSheet = getSpritesheetUrl(classType, specType, treeType);
  return (
    <div className="tree-container">
      <Header treeType={treeType} classType={classType} specType={specType} />
      <div className="talent-tree">
        {Object.entries(tree).map(([key, node], index) => {
          const classType = node.type;
          if (node.spells.length > 0) {
            if (node.spells.length > 1) {
              // Normal selectable spell node.
              for (let i = 0; i < node.spells.length; i++) {
                // Use nodeEntryId for data from WCL and the definition ID for data from the Blizzard API.
                if (
                  selectedTalentNodeIds.includes(node.spells[i].nodeEntryId.toString()) ||
                  selectedTalentNodeIds.includes(node.spells[i].definitionId.toString())
                ) {
                  return (
                    <GridIcon
                      key={index + node.spells[i].nodeEntryId.toString()}
                      gridRow={(node.row + 1).toString()}
                      gridColumn={node.column.toString()}
                      classType={classType}
                      type={"spell"}
                      color={selectedTalents.includes(node.id.toString()) ? "#FF9B00" : null}
                      talentTreeGreyout={!selectedTalents.includes(node.id.toString())}
                      talentSelectedNumber={selectedTalentsValues[selectedTalents.indexOf(node.id.toString())]}
                      talentMaxNumber={node.spells[i].maxRanks.toString()}
                      talentName={node.spells[i].name}
                      talentDescription={
                        node.spells[i].rankDescriptions[
                          parseInt(selectedTalentsValues[selectedTalents.indexOf(node.id.toString())]) - 1
                        ]
                      }
                      id={node.spells[i].spellId}
                      spriteSheet={spriteSheet}
                      sprite={node.spells[i].sprite}
                    />
                  );
                }
              }
            }
            return (
              <GridIcon
                key={index + node.spells[0].nodeEntryId.toString()}
                gridRow={(node.row + 1).toString()}
                gridColumn={node.column.toString()}
                classType={classType}
                type={"spell"}
                color={selectedTalents.includes(node.id.toString()) ? "#FF9B00" : null}
                talentTreeGreyout={!selectedTalents.includes(node.id.toString())}
                talentSelectedNumber={selectedTalentsValues[selectedTalents.indexOf(node.id.toString())]}
                talentMaxNumber={node.spells[0].maxRanks.toString()}
                talentName={node.spells[0].name}
                talentDescription={
                  selectedTalents.includes(node.id.toString())
                    ? node.spells[0].rankDescriptions[
                        parseInt(selectedTalentsValues[selectedTalents.indexOf(node.id.toString())]) - 1
                      ]
                    : node.spells[0].description
                }
                talentDef={node.spells[0].definitionId.toString()}
                id={node.spells[0].spellId}
                spriteSheet={spriteSheet}
                sprite={node.spells[0].sprite}
              />
            );
          }
          return (
            <GridIcon key={index} gridRow={(node.row + 1).toString()} gridColumn={node.column.toString()} classType={classType} />
          );
        })}
        <canvas className="talent-tree-canvas" ref={canvasRef} width="1000" height="1100" />
      </div>
    </div>
  );
};

const GridIcon = (props) => {
  const {
    gridRow,
    gridColumn,
    classType,
    color,
    iconSize,
    id,
    talentTreeGreyout,
    talentSelectedNumber,
    talentMaxNumber,
    talentName,
    talentDescription,
    talentDef,
    spriteSheet,
    sprite,
  } = props;

  const getIcon = (classType) => {
    // Some icons are not correct from wow api. Instead, use hardcoded images that we manually update
    let staticDataImg = `https://static.bigbrain.gg/wow/spell/assets/${id}.webp`;
    const hardcodedIds = [
      392256, // Restoration Druid: Harmonious Blooming
      33763, // Restoration Druid: Lifebloom
      196737, // Brewmaster Monk: High Tolerance
      191837, // Mistweaver Monk: Essence Font
      31850, // Protection Paladin: Ardent Defender
      393114, // Protection Paladin: Improved Ardent Defender
      267344, // Retribution Paladin: Art of War
      108209, // Subtelty Rogue: Shadow Focus
      264119, // Demonology Warlock: Summon Vilefiend
      215571, // Fury Warrior: Frothing Berserker
      392792, // Arms Warrior: Frothing Berserker
      392790, // Protection Warrior: Frothing Berserker
      384042, // Protection Warrior: Unnerving Focus
      207200, // Blood Death Knight: Permafrost
      221536, // Blood Death Knight: Heartbreaker
    ];
    if (hardcodedIds.includes(id)) {
      staticDataImg = `https://static.bigbrain.gg/wow/spell/assets/hardcoded/${id}.webp`;
    } else if (spriteSheet && sprite) {
      staticDataImg = spriteSheet;
    }

    const spriteData = sprite;
    // round choice square
    if (classType == 3 || classType == "choice") {
      return (
        <>
          <MediaQuery min="MOBILE_SMALL" max="MOBILE_MEDIUM">
            <OctagonTalentIcon
              // WHEN RESIZING TREE: change iconSize
              iconSize="25px"
              color={color}
              staticDataImg={staticDataImg}
              talentSelectedNumber={talentSelectedNumber}
              talentMaxNumber={talentMaxNumber}
              isInnerBorderPresent={true}
              spriteData={spriteData}
            />
          </MediaQuery>
          <MediaQuery min="MOBILE_LARGE" max="DESKTOP_LARGE">
            <OctagonTalentIcon
              // WHEN RESIZING TREE: change iconSize
              iconSize={iconSize ? iconSize : "32px"}
              color={color}
              staticDataImg={staticDataImg}
              talentSelectedNumber={talentSelectedNumber}
              talentMaxNumber={talentMaxNumber}
              isInnerBorderPresent={true}
              spriteData={spriteData}
            />
          </MediaQuery>
        </>
      );
    }
    return (
      <>
        <MediaQuery min="MOBILE_SMALL" max="MOBILE_MEDIUM">
          <Icon
            iconSize="25px"
            classType={classType == 2 || classType == "round" ? "circle wow-talent-icon" : "wow-talent-icon"}
            color={color}
            talentTreeGreyout={talentTreeGreyout}
            talentSelectedNumber={talentSelectedNumber}
            talentMaxNumber={talentMaxNumber}
            isInnerBorderPresent={true}
            staticDataImg={staticDataImg}
            spriteData={spriteData}
            // WHEN RESIZING TREE: change margins
            style={{
              marginTop: "-15px",
              marginLeft: "-15px",
            }}
          />
        </MediaQuery>
        <MediaQuery min="MOBILE_LARGE" max="DESKTOP_LARGE">
          <Icon
            // WHEN RESIZING TREE: change iconSize
            iconSize={iconSize ? iconSize : "32px"}
            classType={classType == 2 || classType == "round" ? "circle wow-talent-icon" : "wow-talent-icon"}
            color={color}
            talentTreeGreyout={talentTreeGreyout}
            talentSelectedNumber={talentSelectedNumber}
            talentMaxNumber={talentMaxNumber}
            isInnerBorderPresent={true}
            staticDataImg={staticDataImg}
            spriteData={spriteData}
            // WHEN RESIZING TREE: change margins
            style={{
              marginTop: "-12px",
              marginLeft: "-12px",
            }}
          />
        </MediaQuery>
      </>
    );
  };

  return (
    <div style={{ gridRow: gridRow, gridColumn: gridColumn, zIndex: "100" }}>
      <WowTooltip type="spell" id={id} name={talentName} def={talentDef} rank={talentSelectedNumber}>
        {getIcon(classType)}
      </WowTooltip>
    </div>
  );
};

export default React.memo(WOWTalentTreeSection);
